import { ReactElement } from "react";
import { BrandFilter } from "components/filters/BrandFilter";
import { ModelFilter } from "components/filters/ModelFilter";
import { FilterPriceMax } from "components/information-lister-pages/HomeContent/elements/FilterPriceMax";
import { FilterYearMin } from "components/information-lister-pages/HomeContent/elements/FilterYearMin";
import styled from "styled-components";
import { useCounts, useSearchFilters } from "services/search";
import { Button } from "components/shared/buttons/Button";
import { useForm } from "lib/form";
import { Number } from "components/Number";
import { Loading } from "components/shared/Loading";
import { SearchQuery } from "services/strapi/types";
import { useMediaContext } from "lib/media-query";
import { MEDIA } from "ui/components/shared/constants";
import { SmileyIcon } from "./elements";

type SubmitValues = {
    merkIds: string[];
    modelIds: string[];
    maximumprijs: string[];
    minimumbouwjaar: string[];
};

const MobileImageWrapper = styled.div`
    background-image: url("/static/images/homepage/hero/hero-image-mobile.webp");
    background-position: center;
    background-size: cover;
    height: 20.125rem;
    display: block;
    padding: 1.75rem 1.5rem;
`;

const ContainerWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 3rem 0;
    border: ${({ theme }) => `1px solid ${theme.colors.tertiary[400]}`};
    bottom: 3rem;
    padding: 2rem 1.5rem;
    position: relative;
    width: 100%;
    margin: 0 0 -3rem 0;
    box-shadow: 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.03);

    display: flex;
    flex-direction: column;
    gap: 10px;

    @media (${MEDIA.lg}) {
        display: grid;
        background-image: url("/static/images/homepage/hero/hero-image.webp");
        background-position: center;
        background-position-y: -15rem;
        border-radius: 5rem 0.75rem;
        bottom: 0rem;
        gap: 5rem;
        grid-template-columns: 2fr 1fr;
        height: 35rem;
        margin: 2rem 0 0 0;
        padding: 4rem;
        box-shadow: unset;
    }
`;

const FilterWrapper = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    gap: 0.625rem;
    display: flex;
    flex-direction: column;

    @media (${MEDIA.lg}) {
        width: 12.6875rem;
    }
`;

const IconWrapper = styled.div`
    align-items: center;
    background-color: ${({ theme }) => theme.colors.primary[700]};
    border-radius: 0rem 0.75rem 0.75rem 0.75rem;
    display: flex;
    gap: 0.25rem;
    padding: 0.25rem 1rem;
    width: fit-content;

    > svg {
        height: 1rem;
        width: 1.625rem;
    }

    @media (${MEDIA.lg}) {
        gap: 0.75rem;
        height: 76px;
        padding: 1.125rem 1rem;

        > svg {
            height: 2rem;
            width: 3.25rem;
        }
    }
`;

const IconText = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    flex: 0;
    font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
    font-size: 1.5rem;
    font-style: normal;
    font-weight: 600;

    @media (${MEDIA.lg}) {
        font-size: 3.5rem;
    }
`;

const InputsContainer = styled.div`
    border-radius: 3.75rem 0.5rem;
    display: grid;
    gap: 0.5rem;

    @media (${MEDIA.lg}) {
        background-color: ${({ theme }) => theme.colors.white};
        column-gap: 0.62rem;
        grid-template-columns: 1fr 1fr;
        padding: 2rem;
        row-gap: 1.25rem;
        max-height: 19.75rem;
    }
`;

const Label = styled.span`
    color: ${({ theme }) => theme.colors.black};
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.25rem;
`;

const LoaderWrapper = styled.div`
    float: right;
    margin: -2.75rem -32px 0 5px;
    width: 20%;
`;

const Header = styled.h1`
    color: ${({ theme }) => theme.colors.tertiary[100]};
    font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
    font-size: 2.25rem;
    font-weight: 600;

    @media (${MEDIA.lg}) {
        font-size: 3.75rem;
    }
`;

const StyledButton = styled(Button)`
    align-items: center;
    display: flex;
    font-weight: 600;
    height: 3rem;
    justify-content: center;
    margin-top: 0.75rem;
    padding: 0.96875rem 1.875rem;
    border-radius: 0.5rem;
    width: 100%;

    @media (${MEDIA.lg}) {
        grid-column: 1/3;
        margin-top: 0;
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;

    @media (${MEDIA.lg}) {
        padding: 0 7rem 0 0;
    }
`;

const PriceWrapper = styled.div`
    & > * input {
        border-color: ${({ theme }) => `${theme.colors.black} !important`};
        border-radius: 0.5rem !important;
        padding: 0 0 0 1.625rem !important;
        font-size: 1rem;
        font-weight: 400;

        &:focus {
            border: ${({ theme }) => `1px ${theme.colors.secondary[350]} solid !important`};
        }
    }
`;

export const HomeSearchHeader = (): ReactElement => {
    const [, setSearchFilters] = useSearchFilters();
    const [counts] = useCounts(true);
    const mediaContext = useMediaContext();
    const isMobilePhone = mediaContext.isMobile || mediaContext.isMobilePlus;

    const [submit, isSubmitting] = useForm({
        onChange: (values: SubmitValues) => {
            setSearchFilters((searchFilters: SearchQuery) => ({ ...searchFilters, ...values }));
        }
    });

    if (isMobilePhone) {
        return (
            <>
                <MobileImageWrapper>
                    <TextContainer>
                        <Header>Vind makkelijk je ideale auto.</Header>
                        <IconWrapper>
                            <SmileyIcon />
                            <IconText>Check.</IconText>
                        </IconWrapper>
                    </TextContainer>
                </MobileImageWrapper>
                <ContainerWrapper>
                    <FilterWrapper>
                        <Label>Merk</Label>
                        <BrandFilter isHomepage />
                    </FilterWrapper>
                    <FilterWrapper>
                        <Label>Model</Label>
                        <ModelFilter isHomepage forceDisabledPlaceholder />
                    </FilterWrapper>
                    <FilterWrapper>
                        <Label>Prijs tot</Label>
                        <PriceWrapper>
                            <FilterPriceMax />
                        </PriceWrapper>
                    </FilterWrapper>
                    <FilterWrapper>
                        <Label>Bouwjaar vanaf</Label>
                        <FilterYearMin isHomepage />
                    </FilterWrapper>
                    <StyledButton disabled={isSubmitting} onClick={submit}>
                        Toon {counts?.total ? <Number value={counts.total} /> : "de"} resultaten
                        {isSubmitting && (
                            <LoaderWrapper>
                                <Loading />
                            </LoaderWrapper>
                        )}
                    </StyledButton>
                </ContainerWrapper>
            </>
        );
    }

    return (
        <ContainerWrapper>
            <TextContainer>
                <Header>Vind makkelijk je ideale auto.</Header>
                <IconWrapper>
                    <SmileyIcon />
                    <IconText>Check.</IconText>
                </IconWrapper>
            </TextContainer>
            <InputsContainer>
                <FilterWrapper>
                    <Label>Merk</Label>
                    <BrandFilter isHomepage />
                </FilterWrapper>
                <FilterWrapper>
                    <Label>Model</Label>
                    <ModelFilter isHomepage forceDisabledPlaceholder />
                </FilterWrapper>
                <FilterWrapper>
                    <Label>Prijs tot</Label>
                    <PriceWrapper>
                        <FilterPriceMax />
                    </PriceWrapper>
                </FilterWrapper>
                <FilterWrapper>
                    <Label>Bouwjaar vanaf</Label>
                    <FilterYearMin isHomepage />
                </FilterWrapper>
                <StyledButton disabled={isSubmitting} onClick={submit}>
                    Toon {counts?.total ? <Number value={counts.total} /> : "de"} resultaten
                    {isSubmitting && (
                        <LoaderWrapper>
                            <Loading />
                        </LoaderWrapper>
                    )}
                </StyledButton>
            </InputsContainer>
        </ContainerWrapper>
    );
};
